import { useCallback, useMemo } from "react";
import { useTunnelData } from "../TunnelContext";
import { pushToDataLayer, clearEcommerceData } from "./gtmDataLayer";
import { FUNNEL_EVENTS, STEP_NAMES, CLUB_STATUS_OPEN } from "./eventDefinitions";
import { formatCartForEcommerce, getTotalValue } from "./ecommerceHelpers";

/**
 * Single facade for ON AIR funnel tracking.
 *
 * It reproduces the exact event names and payloads historically emitted inline
 * (funnel_step_1..5, funnel_step_final), and brings the GA4-conformity fixes
 * from the resamania2-pass-app analytics module: ecommerce wrapper,
 * transaction_id, values in euros and clearEcommerceData() before ecommerce
 * events. There is no config gating: ON AIR loads GTM unconditionally (after
 * Didomi) and pushToDataLayer already no-ops when the dataLayer is absent.
 *
 * add_to_cart lives in TunnelContext (emitted from a provider method where this
 * hook cannot run) and uses the low-level helpers directly.
 */
export function useAnalytics() {
  const { tunnel } = useTunnelData();

  const meta = useMemo(
    () => ({
      clubName: tunnel.club?.name || "",
      aboName: tunnel.offer?.offer?.name || "",
    }),
    [tunnel.club, tunnel.offer]
  );

  const subscriptionBase = useCallback(
    () => ({
      step_name: STEP_NAMES.SUBSCRIPTION,
      funnel_club: meta.clubName,
      funnel_club_status: CLUB_STATUS_OPEN,
      funnel_abo: meta.aboName,
    }),
    [meta]
  );

  const trackStep1 = useCallback(() => {
    pushToDataLayer(FUNNEL_EVENTS.STEP_1, { step_name: STEP_NAMES.CLUB });
  }, []);

  const trackStep2 = useCallback(() => {
    pushToDataLayer(FUNNEL_EVENTS.STEP_2, {
      step_name: STEP_NAMES.SUBSCRIPTION,
      funnel_club: meta.clubName,
      funnel_club_status: CLUB_STATUS_OPEN,
    });
  }, [meta]);

  const trackStep3 = useCallback(() => {
    pushToDataLayer(FUNNEL_EVENTS.STEP_3, subscriptionBase());
  }, [subscriptionBase]);

  const trackStep4 = useCallback(() => {
    pushToDataLayer(FUNNEL_EVENTS.STEP_4, subscriptionBase());
  }, [subscriptionBase]);

  const trackStep4Sepa = useCallback(() => {
    pushToDataLayer(FUNNEL_EVENTS.STEP_4_SEPA, subscriptionBase());
  }, [subscriptionBase]);

  const trackStep4Sign = useCallback(() => {
    pushToDataLayer(FUNNEL_EVENTS.STEP_4_SIGN, subscriptionBase());
  }, [subscriptionBase]);

  const trackStep5 = useCallback(() => {
    pushToDataLayer(FUNNEL_EVENTS.STEP_5, subscriptionBase());
  }, [subscriptionBase]);

  const trackPurchase = useCallback(() => {
    const totalTI = tunnel.sales?.totalTI ?? 0;
    const totalTE = tunnel.sales?.totalTE ?? 0;
    const value = getTotalValue(tunnel);
    const tax = (totalTI - totalTE) / 100;
    const transactionId = tunnel.sales?.["@id"];
    const items = formatCartForEcommerce(tunnel);

    clearEcommerceData();
    pushToDataLayer(FUNNEL_EVENTS.PURCHASE, {
      // Top-level keys preserved for existing ON AIR tags (value now in euros)
      currency: "EUR",
      value,
      tax,
      transaction_id: transactionId,
      items,
      // GA4-conform wrapper
      ecommerce: {
        transaction_id: transactionId,
        currency: "EUR",
        value,
        tax,
        items,
      },
    });
  }, [tunnel]);

  const trackFinal = useCallback(
    ({ em, ph, fn, ln, zp, ct } = {}) => {
      pushToDataLayer(FUNNEL_EVENTS.STEP_FINAL, {
        ...subscriptionBase(),
        em,
        ph,
        fn,
        ln,
        zp,
        ct,
      });
    },
    [subscriptionBase]
  );

  return {
    trackStep1,
    trackStep2,
    trackStep3,
    trackStep4,
    trackStep4Sepa,
    trackStep4Sign,
    trackStep5,
    trackPurchase,
    trackFinal,
  };
}
